/**
 * The `<attributes>` of a bar: what changes about the staves themselves.
 *
 * Divisions, key, metre, how many staves the part has, the clef on each of them, what each
 * staff is and how it is tuned, and what the instrument transposes. The first bar states
 * all of it, because a file whose first bar states no metre has no defined length; later
 * bars state only what actually changed there.
 */

import { clefAt, instrumentAt, resolveAttributes } from "../document/query";
import { transposes } from "../document/pitch";
import type { Transposition } from "../document/pitch";
import { partKey } from "../document/key";
import { C_MAJOR } from "../document/types";
import type { Clef, Part } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { staffDetailsNode } from "./export-tab";
import { ossiaDetailsNode } from "./export-ossia";
import type { StaffMap } from "./export-ossia";
import type { ExportContext } from "./export-measure";

function clefNode(clef: Clef, staff: number | null): XmlNode {
  return el("clef", { number: staff }, [
    leaf("sign", clef.sign),
    leaf("line", clef.line),
    clef.octaveChange === 0 ? null : leaf("clef-octave-change", clef.octaveChange),
  ]);
}

/**
 * `<attributes>` for a bar, or `null` when nothing changes in it.
 *
 * The first bar states everything -- divisions, key, metre, staff count, every clef and
 * the part's transposition -- because a file whose first bar states no metre has no
 * defined length. Later bars state only what actually changed.
 */
export function attributesNode(context: ExportContext, index: number): XmlNode | null {
  const { score, part, divisions } = context;
  const global = score.measures[index];
  if (global === undefined) return null;
  const first = index === 0;
  const staves = context.staves;
  const numbered = staves.count > 1;
  const changes = part.measures[global.id]?.clefChanges ?? {};
  const instrument = instrumentAt(score, part, index);
  const moved = movesTheSignature(context, index);

  // The piece's key is the concert one; what this part prints is that key moved by what
  // the instrument transposes, which is the only signature its player can read the notes
  // against. A part at pitch is unmoved, so this is the same signature for most of them.
  // A bar where the player picks up an instrument reading at another pitch restates the
  // signature even where the piece's key holds, because the printed one has just moved.
  const held = moved ? resolveAttributes(score, index).key : null;
  const stated = first ? (global.key ?? C_MAJOR) : (global.key ?? held);
  const key = stated === null ? null : partKey(stated, instrument);
  const time = first ? (global.time ?? { beats: 4, beatType: 4, symbol: "number" }) : global.time;
  // An ossia is read in the clef of the staff it stands over, and a bar that changes that
  // clef changes it on both: the alternative reading is the same music read the same way.
  const clefs: XmlNode[] = [];
  const clefsFor = (at: number, clef: Clef): void => {
    const ossia = staves.ossia.get(at);
    if (ossia !== undefined) clefs.push(clefNode(clef, numbered ? ossia : null));
    clefs.push(clefNode(clef, numbered ? (staves.played[at] ?? at + 1) : null));
  };
  if (first) {
    for (let at = 0; at < part.staves.length; at += 1) clefsFor(at, clefAt(score, part, index, at));
  } else {
    for (const [at, clef] of Object.entries(changes)) clefsFor(Number(at), clef);
  }

  const children = [
    first ? leaf("divisions", divisions) : null,
    key === null
      ? null
      : el("key", {}, [
          leaf("fifths", key.fifths),
          key.mode === null ? null : leaf("mode", key.mode),
        ]),
    time === null
      ? null
      : el("time", { symbol: time.symbol === "number" ? null : time.symbol }, [
          leaf("beats", time.beats),
          leaf("beat-type", time.beatType),
        ]),
    first && numbered ? leaf("staves", staves.count) : null,
    ...clefs,
    ...(first ? staffDetails(part, staves, numbered) : []),
    (first && transposes(instrument.transposition)) || moved
      ? transposeNode(instrument.transposition)
      : null,
    // Last of the attributes, which is where the DTD puts it.
    multiRestNode(context, index),
  ].filter((child): child is XmlNode => child !== null);

  return children.length === 0 ? null : el("attributes", {}, children);
}

/**
 * `<measure-style>` saying that this bar opens a run of silent bars drawn as one.
 *
 * The count is the number a player reads off the bar. Only the head of the run says
 * anything; the bars it covers are written as the silent bars they are.
 */
function multiRestNode(context: ExportContext, index: number): XmlNode | null {
  const count = context.restRuns.get(index);
  if (count === undefined) return null;
  return el("measure-style", {}, [leaf("multiple-rest", count)]);
}

/**
 * The staves that say something about themselves: a drum's one line, a guitar's strings,
 * and every ossia staff, which always says something -- that it is an ossia.
 */
function staffDetails(part: Part, staves: StaffMap, numbered: boolean): XmlNode[] {
  const nodes: XmlNode[] = [];
  part.staves.forEach((staff, index) => {
    const ossia = staves.ossia.get(index);
    if (ossia !== undefined) nodes.push(ossiaDetailsNode(part, index, ossia));
    const number = numbered ? (staves.played[index] ?? index + 1) : null;
    const node = staffDetailsNode(staff.lines, number, part.instrument.tuning);
    if (node !== null) nodes.push(node);
  });
  return nodes;
}

/**
 * Whether the instrument taken up in this bar reads at a different pitch from the one
 * before it -- which is the only case where a bar has to restate `<transpose>` and the
 * signature that goes with it. Picking up a bass clarinet from a clarinet changes neither.
 */
function movesTheSignature(context: ExportContext, index: number): boolean {
  if (index === 0 || !context.instruments.changes.has(index)) return false;
  const { score, part } = context;
  const now = instrumentAt(score, part, index).transposition;
  const before = instrumentAt(score, part, index - 1).transposition;
  return (
    now.diatonic !== before.diatonic ||
    now.chromatic !== before.chromatic ||
    now.octaveChange !== before.octaveChange
  );
}

function transposeNode(shift: Transposition): XmlNode {
  return el("transpose", {}, [
    leaf("diatonic", shift.diatonic),
    leaf("chromatic", shift.chromatic),
    shift.octaveChange === 0 ? null : leaf("octave-change", shift.octaveChange),
  ]);
}
